// DetallePlano.jsx
import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    FiDownload, FiAlertTriangle, FiTool, FiLayers,
    FiHome, FiArrowLeft, FiShare2, FiPrinter, FiGrid, FiChevronRight
} from 'react-icons/fi';
import SEO from '../components/SEO';
import { planosGratisData } from '../data/planosGratisData';
import { mapPlanoToOldStructure } from '../data/planosMapper'; // 👈 Importa el mapper

const FADE_IN_VARIANTS = {
    hidden: { opacity: 0, y: 30 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.5,
            ease: [0.22, 1, 0.36, 1]
        }
    }
};

/*const SPECIFICATION_ICONS = {
    areaTerreno: FiTool,
    areaConstruida: FiLayers,
    ambientes: FiHome
};*/

const DetallePlano = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [plano, setPlano] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [imagenActiva, setImagenActiva] = useState(0);

    useEffect(() => {
        try {
            setLoading(true);
            // Buscar el plano original con la nueva estructura
            const foundPlanoOriginal = planosGratisData.find((p) => {
                // Generar slug del nombre para comparar
                const planoSlug = p.name?.toLowerCase().replace(/\s+/g, '-') || '';
                return planoSlug === slug;
            });

            if (!foundPlanoOriginal) {
                // Intentar buscar por id como fallback
                const foundById = planosGratisData.find((p) => p.id?.toString() === slug);
                if (foundById) {
                    const mappedPlano = mapPlanoToOldStructure(foundById);
                    setPlano(mappedPlano);
                    setError(null);
                } else {
                    setError('Plano no encontrado');
                }
            } else {
                // Mapear a la estructura esperada
                const mappedPlano = mapPlanoToOldStructure(foundPlanoOriginal);
                setPlano(mappedPlano);
                setError(null);
            }
        } catch (err) {
            setError('Error al cargar el plano');
            console.error('Error loading plan:', err);
        } finally {
            setLoading(false);
        }
    }, [slug]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [slug]);

    const handleDownload = (e) => {
        if (!plano) return;

        if (window.gtag) {
            window.gtag('event', 'download', {
                event_category: 'Plano',
                event_label: plano.titulo,
                value: plano.categoria
            });
        }
    };

    const handleShare = async () => {
        if (!plano) return;

        const shareData = {
            title: plano.titulo,
            text: `Descubre el plano de ${plano.titulo} en Planix`,
            url: window.location.href
        };

        try {
            if (navigator.share) {
                await navigator.share(shareData);
            } else {
                await navigator.clipboard.writeText(window.location.href);
                alert('¡Enlace copiado al portapapeles!');
            }
        } catch (error) {
            console.error('Error sharing:', error);
        }
    };

    if (loading) {
        return (
            <div className="pt-40 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-[#F26B1D] border-t-transparent" />
                <p className="mt-4 text-gray-500">Cargando plano...</p>
            </div>
        );
    }

    if (error || !plano) {
        return (
            <div className="pt-40 text-center max-w-md mx-auto px-6">
                <FiAlertTriangle className="text-5xl text-amber-500 mx-auto mb-4" />
                <p className="text-xl font-bold text-[#1C1C1C]">{error || 'Plano no encontrado'}</p>
                <p className="text-gray-500 mt-2">El plano que buscas no está disponible o ha sido movido.</p>
                <button
                    onClick={() => navigate('/planos-gratis')}
                    className="mt-6 inline-flex items-center gap-2 bg-[#F26B1D] text-white px-6 py-3 rounded-xl hover:bg-[#E05D1A] transition-colors"
                >
                    <FiArrowLeft /> Volver a la lista
                </button>
            </div>
        );
    }

    const downloadUrl = plano.driveId;

    // ✅ Función para generar especificaciones mejoradas con datos de la nueva estructura
    const getSpecifications = (plano) => {
        const specs = {
            areaTerreno: plano.front && plano.depth ? `${plano.front} x ${plano.depth} m` : 'N/A',
            areaConstruida: plano.area ? `${plano.area} m²` : 'N/A',
            ambientes: plano.rooms ? `${plano.rooms} ambientes` : 'N/A'
        };

        // Si el plano tiene más datos, agregarlos
        if (plano.floors) {
            specs['pisos'] = `${plano.floors} niveles`;
        }
        if (plano.style) {
            specs['estilo'] = plano.style;
        }
        if (plano.formats) {
            specs['formatos'] = plano.formats.join(', ');
        }

        return specs;
    };

    const specifications = getSpecifications(plano);

    // ✅ Función para renderizar especificaciones con iconos
    const renderSpecification = (key, value) => {
        const iconMap = {
            areaTerreno: FiTool,
            areaConstruida: FiLayers,
            ambientes: FiHome
        };

        const labels = {
            areaTerreno: 'Área Terreno',
            areaConstruida: 'Área Construida',
            ambientes: 'Ambientes Principales',
            pisos: 'Niveles',
            estilo: 'Estilo',
            formatos: 'Formatos'
        };

        const Icon = iconMap[key] || FiTool;

        return (
            <div
                key={key}
                className={`bg-gray-50 p-3 sm:p-4 rounded-xl flex items-center gap-3 border border-gray-100 
                    ${key === 'ambientes' || key === 'formatos' ? 'col-span-2' : ''}`}
            >
                <Icon className="text-[#F26B1D] text-xl flex-shrink-0" />
                <div className="min-w-0">
                    <span className="block text-[10px] sm:text-xs text-gray-400 uppercase tracking-wider">
                        {labels[key] || key}
                    </span>
                    <span className="block font-bold text-[#1C1C1C] truncate">
                        {value}
                    </span>
                </div>
            </div>
        );
    };

    return (
        <motion.div
            initial="hidden"
            animate="visible"
            variants={FADE_IN_VARIANTS}
            className="pt-32 pb-20 max-w-6xl mx-auto px-4 sm:px-6"
        >
            <SEO
                title={`${plano.titulo} | Planos de Referencia Gratis | Planix`}
                description={`Detalles técnicos y descarga del ${plano.titulo}. ${plano.descripcionCompleta}`}
                image={plano.imagenes?.[0] || '/images/placeholder-plan.jpg'}
                canonical={`https://www.planixapp.com/planos-gratis/${plano.slug}`}
                keywords={`${plano.titulo}, planos vivienda, arquitectura Planix, descarga planos`}
                type="article"
            />

            {/* Breadcrumbs */}
            <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6 overflow-x-auto">
                <Link to="/" className="hover:text-[#F26B1D] transition-colors">Inicio</Link>
                <span className="text-gray-300">/</span>
                <Link to="/planos-gratis" className="hover:text-[#F26B1D] transition-colors">Planos Gratis</Link>
                <span className="text-gray-300">/</span>
                <span className="text-[#F26B1D] font-medium truncate">{plano.titulo}</span>
            </nav>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
                {/* Columna izquierda */}
                <div className="space-y-6">
                    <div>
                        <span className="inline-block text-[#F26B1D] font-bold text-xs uppercase tracking-widest bg-[#F26B1D]/10 px-4 py-1.5 rounded-full">
                            {plano.categoria}
                        </span>
                        <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-[#1C1C1C] mt-4 tracking-tight">
                            {plano.titulo}
                        </h1>
                    </div>

                    <div className="bg-gray-100 rounded-2xl lg:rounded-3xl overflow-hidden shadow-inner aspect-[4/3] border border-gray-200 relative">
                        <img
                            src={plano.imagenes?.[imagenActiva] || '/images/placeholder-plan.jpg'}
                            alt={`Plano de ${plano.titulo} - Imagen ${imagenActiva + 1}`}
                            className="w-full h-full object-cover transition-transform duration-500"
                            loading="lazy"
                            onError={(e) => { e.target.src = '/images/placeholder-plan.jpg'; }}
                        />

                        {/* Controles del carrusel (solo si hay más de 1 imagen) */}
                        {plano.imagenes?.length > 1 && (
                            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                                {plano.imagenes.map((_, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => setImagenActiva(idx)}
                                        className={`w-2.5 h-2.5 rounded-full transition-all ${idx === imagenActiva ? 'bg-[#F26B1D] w-6' : 'bg-white/60'
                                            }`}
                                        aria-label={`Ver imagen ${idx + 1}`}
                                    />
                                ))}
                            </div>
                        )}

                        {/* Flechas de navegación */}
                        {plano.imagenes?.length > 1 && (
                            <>
                                <button
                                    onClick={() => setImagenActiva((prev) => (prev === 0 ? plano.imagenes.length - 1 : prev - 1))}
                                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/30 text-white p-2 rounded-full hover:bg-black/50 transition"
                                >
                                    ◀
                                </button>
                                <button
                                    onClick={() => setImagenActiva((prev) => (prev === plano.imagenes.length - 1 ? 0 : prev + 1))}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/30 text-white p-2 rounded-full hover:bg-black/50 transition"
                                >
                                    ▶
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {/* Columna derecha */}
                <div className="bg-white p-6 sm:p-8 rounded-2xl lg:rounded-3xl border border-gray-100 shadow-sm sticky top-36">
                    {/* Especificaciones */}
                    <div className="mb-8">
                        <h2 className="text-xl sm:text-2xl font-bold text-[#1C1C1C] mb-4">
                            Especificaciones Técnicas
                        </h2>
                        <div className="grid grid-cols-2 gap-3 sm:gap-4">
                            {Object.entries(specifications).map(([key, value]) => {
                                if (key === 'formatos') {
                                    // Renderizar formatos como badges
                                    return (
                                        <div key={key} className="col-span-2">
                                            <span className="block text-[10px] sm:text-xs text-gray-400 uppercase tracking-wider mb-1">
                                                Formatos disponibles
                                            </span>
                                            <div className="flex gap-2 flex-wrap">
                                                {value.split(', ').map((format, idx) => (
                                                    <span key={idx} className="bg-[#F26B1D]/10 text-[#F26B1D] px-3 py-1 rounded-full text-xs font-bold">
                                                        {format}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                }
                                return renderSpecification(key, value);
                            })}
                        </div>
                    </div>

                    {/* Descripción */}
                    <div className="mb-8">
                        <h2 className="text-xl sm:text-2xl font-bold text-[#1C1C1C] mb-2">
                            Descripción del Diseño
                        </h2>
                        <p className="text-gray-600 text-sm leading-relaxed">
                            {plano.descripcionCompleta}
                        </p>
                    </div>

                    {/* Advertencia */}
                    <div className="mb-8 p-4 sm:p-5 bg-amber-50 rounded-xl sm:rounded-2xl border border-amber-100 flex items-start gap-3 text-amber-900">
                        <FiAlertTriangle className="text-amber-500 text-xl flex-shrink-0 mt-0.5" />
                        <p className="text-xs leading-relaxed">
                            <strong>Importante:</strong> Este es un plano de referencia arquitectónica. Antes de iniciar cualquier construcción o trámite municipal, es obligatorio que un Ingeniero Civil o Arquitecto colegiado valide el diseño, realice el cálculo estructural y adapte los planos a las normativas de tu zona.
                        </p>
                    </div>

                    {/* Botones de acción */}
                    <div className="space-y-3">
                        <a
                            href={downloadUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={handleDownload}
                            className="w-full bg-[#1C1C1C] hover:bg-[#F26B1D] text-white font-bold py-3.5 sm:py-4 px-6 rounded-xl transition-all duration-300 flex items-center justify-center gap-3 text-xs uppercase tracking-widest shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                        >
                            <FiDownload className="text-lg" />
                            Descargar Archivo de Referencia
                        </a>

                        <div className="flex gap-3">
                            <button
                                onClick={handleShare}
                                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2.5 px-4 rounded-xl transition-colors flex items-center justify-center gap-2 text-sm"
                                aria-label="Compartir"
                            >
                                <FiShare2 /> Compartir
                            </button>
                            <Link
                                to={'/planos'}
                                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2.5 px-4 rounded-xl transition-colors flex items-center justify-center gap-2 text-sm"
                            >
                                <FiPrinter /> Ver Planos
                            </Link>
                        </div>
                    </div>

                    {/* ✅ PRIMER ENLACE: "Ver más planos" dentro de la tarjeta */}
                    <div className="mt-6 pt-6 border-t border-gray-200">
                        <Link
                            to="/planos-gratis"
                            className="inline-flex items-center gap-2 text-sm font-medium text-[#F26B1D] hover:text-[#E05D1A] transition-colors group w-full justify-center"
                        >
                            <FiGrid className="text-lg group-hover:rotate-12 transition-transform" />
                            Ver todos los planos disponibles
                            <FiChevronRight className="group-hover:translate-x-1 transition-transform" />
                        </Link>
                    </div>
                </div>
            </div>

            {/* ✅ SEGUNDO ENLACE: "Ver más Planos" al final */}
            <div className="mt-16 text-center">
                <p className="text-gray-600 text-sm">
                    ¿Buscas más inspiración?
                </p>
                <Link
                    to="/planos"
                    className="inline-flex items-center gap-2 text-[#F26B1D] hover:text-[#E05D1A] font-bold text-lg transition-colors mt-2 group"
                >
                    Ver más Planos
                    <FiChevronRight className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <span className="block text-xs text-gray-400 mt-1">
                    Explora nuestra colección completa de planos de referencia
                </span>
            </div>

            {/* ✅ TERCER ENLACE: Planos relacionados */}
            <div className="mt-20 pt-10 border-t border-gray-100">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-[#1C1C1C]">
                        Servicios que te puedan interesar
                    </h2>
                    <Link
                        to="/"
                        className="text-sm text-[#F26B1D] hover:text-[#E05D1A] font-medium flex items-center gap-1 transition-colors"
                    >
                        Servicios <FiChevronRight />
                    </Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <p className="text-gray-500 text-center col-span-full py-8">
                        Próximamente más planos disponibles
                    </p>
                </div>
            </div>
        </motion.div>
    );
};

export default DetallePlano;